import { useCallback, useMemo } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'

import type { ReactElement } from 'react'
import type { IChartConfigApiModel, IDataBuilderConfig } from '~/types/graph-builder'

import CompareCohortsFilters from './CompareCohortsFilters'

import ContentLayout from '~/components/ContentLayout'
import DeprecationBanner from '~/components/DeprecationBanner'
import DynamicDataBlockWithCompare from '~/components/DynamicDataBlockWithCompare'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import ViewTitle from '~/components/ViewTitle'
import { COMMON_GRID_PROPS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useChartConfigsListQuery } from '~/queries/graphs-builder'
import { COHORT_TYPE } from '~/types/compare-cohorts-context'
import { Mode, CA_PAGES, CA_SECTIONS, CA_SECTIONS_LABELS_MAPPING } from '~/types/graph-builder'
import { transformByDeviceType } from '~/utils/application'
import { formatConfig } from '~/utils/graphs-builder'
import { getDataLayoutPosition } from '~/utils/grid'

import './styles.scss'

const ResponsiveGridLayout = WidthProvider(Responsive)

const PAGE = CA_PAGES.OVERVIEW_METRICS

const gridItemCommonProps = {
  isDraggable: false,
  isResizable: false,
}

const configs: IChartConfigApiModel[] = [
  {
    applications: [],
    column: 0,
    created_at: 1710838669000,
    created_by: '',
    force_kpi_field: '',
    graph_config_id: '',
    how: '',
    line: 0,
    mode: Mode.NORMAL,
    nb_last_days_to_ignore: 0,
    page: 'overview-metrics',
    position: 0,
    related_glossary_items: [],
    required_breakdown: '',
    section: '',
    series: [
      {
        default_breakdown: '',
        graph_type: 'raw',
        min_nbu: 0,
        normalized_by: [],
        values: ['nbu'],
        with_inf: false,
        with_sup: false,
        with_trend: false,
        y_axes_name: '0',
      },
    ],
    title: 'NBU',
    updated_at: 1710838669000,
    updated_by: '',
    want_negative_evolution: false,
    what: '',
    y_axes: [
      {
        name: '0',
        precision: 0,
        unit: 'raw',
      },
    ],
  },
]

const CompareCohorts = (): ReactElement => {
  /** Local state */

  const { appDetails, appKey, deviceType, isLoading: isAppSummaryLoading } = useAppSummary()

  /** Queries & Mutations */

  const { data: chartConfigs, isLoading } = useChartConfigsListQuery({
    appKey,
    deviceType,
    pageName: PAGE,
  })

  const formattedConfigs = useMemo(
    () => formatConfig(chartConfigs?.data) as unknown as IDataBuilderConfig[],
    [chartConfigs?.data]
  )

  const nbConfigs = formatConfig(configs)

  /** Effects */

  useDocumentTitle('CA Compare Cohorts')

  /** Handlers */

  const getGraphOptions = useCallback(
    (config: IDataBuilderConfig) => ({
      appKey,
      config,
      deviceType,
      isAppLoading: isAppSummaryLoading,
      kpiField: 'installation_date',
    }),
    [appKey, deviceType, isAppSummaryLoading]
  )

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="compare-cohorts-view">
        <ViewTitle>Cohort Comparison</ViewTitle>
        <DeprecationBanner className="compare-cohorts-view__deprecation-banner" />
        <ContentLayout loading={isAppSummaryLoading || isLoading}>
          <div className="filters-container">
            <CompareCohortsFilters cohort={COHORT_TYPE.COHORT_1} />
            <CompareCohortsFilters cohort={COHORT_TYPE.COHORT_2} />
          </div>
          <Section title="">
            <ResponsiveGridLayout {...COMMON_GRID_PROPS} {...gridItemCommonProps} compactType={null}>
              {formattedConfigs
                .filter(({ section }) => !section)
                .map((config, idx) => (
                  <div
                    data-grid={getDataLayoutPosition(`section-1-${idx}`, { x: config.column, y: config.line })}
                    key={idx}
                  >
                    <DynamicDataBlockWithCompare {...getGraphOptions(config as IDataBuilderConfig)} />
                  </div>
                ))}
            </ResponsiveGridLayout>
          </Section>
          <Section title="Cohort infos">
            <ResponsiveGridLayout {...COMMON_GRID_PROPS} {...gridItemCommonProps} compactType={null}>
              {nbConfigs.map((config, idx) => (
                <div
                  data-grid={getDataLayoutPosition(`section-2-${idx}`, { x: config.column, y: config.line })}
                  key={idx + 1}
                >
                  <DynamicDataBlockWithCompare {...getGraphOptions(config as IDataBuilderConfig)} noMenu />
                </div>
              ))}
            </ResponsiveGridLayout>
          </Section>
          {Object.entries(CA_SECTIONS[PAGE]).map(([key, value]) => (
            <Section
              key={value}
              title={CA_SECTIONS_LABELS_MAPPING[PAGE] ? CA_SECTIONS_LABELS_MAPPING[PAGE][key] : value}
            >
              <ResponsiveGridLayout {...COMMON_GRID_PROPS} {...gridItemCommonProps} compactType={null}>
                {formattedConfigs
                  .filter(({ section: s }) => s === value)
                  .map((config, idx) => (
                    <div
                      data-grid={getDataLayoutPosition(`section-3-${idx}`, { x: config.column, y: config.line })}
                      key={idx}
                    >
                      <DynamicDataBlockWithCompare {...getGraphOptions(config as IDataBuilderConfig)} />
                    </div>
                  ))}
              </ResponsiveGridLayout>
            </Section>
          ))}
        </ContentLayout>
      </Layout>
    </>
  )
}

export default CompareCohorts
